import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useApi } from "@lending/lib/api";
import type { CreditCardApplication } from "@lending/types";

const DEFAULT_OPTIONS = {
  enabled: true,
} as const;

/**
 * Polling cadence for the query. A function receives the latest application so
 * callers can stop polling on a terminal state or after a timeout by returning
 * `false` (see useCreditPullTimeout).
 */
type RefetchInterval =
  | number
  | false
  | ((application: CreditCardApplication | undefined) => number | false);

interface Options {
  applicationUuid?: string;
  enabled?: boolean;
  refetchInterval?: RefetchInterval;
}
function useGetCreditCardApplicationQuery(optionsProp: Options = DEFAULT_OPTIONS) {
  const $api = useApi();
  const storeApplicationId = useLendingApplicationID();

  const options = { ...DEFAULT_OPTIONS, ...optionsProp };

  const { applicationUuid, enabled, refetchInterval } = options;
  const resolvedApplicationId = applicationUuid ?? storeApplicationId;

  return $api.useQuery(
    "get",
    "/v2/lending/credit_card_applications/{uuid}/",
    {
      params: {
        path: {
          uuid: resolvedApplicationId,
        },
      },
    },
    {
      enabled: Boolean(resolvedApplicationId) && enabled,
      refetchInterval:
        typeof refetchInterval === "function"
          ? (query) => refetchInterval(query.state.data)
          : refetchInterval,
    }
  );
}

export { useGetCreditCardApplicationQuery };
